package com.oasisfeng.invaluable

/**
 * Graph-free link ontology.
 *
 * Public APIs start at `Link`, the kernel still has a private root ([RawLink]) so it can generate the full
 * `root | link(parent, target)` topology, but that root never appears as a public type or module-level constant.
 *
 * The current implementation uses a simple process-local interner so graph-free structural identity is cheap
 * inside one runtime. This is still not a wire identity: transport and persistence must use a selected
 * LinkProjection scheme when structure crosses process boundaries.
 */
sealed interface Link : Linkable {

    fun parentOrNull(): Link?
    fun isChildOf(parent: Link): Boolean = parentOrNull() == parent

    /**
     * The graph-visible target of this Link.
     *
     * A few kernel products, such as genesis seeds and [Quantity], target the private root.
     * They are still Links because they can parent and target other public Links,
     * but they cannot expose that raw target through normal public navigation.
     */
    fun target(): Link
    fun nestedTargetOrNull(): NestedLink?
    fun requireNestedTarget(): NestedLink

    /**
     * Narrow this instance to a [NestedLink] whose parent is a [Link].
     * This is the normal shape for callers that want to move upward without leaking root into the parent API.
     */
    fun nestedOrNull(): NestedLink? = this as? NestedLink
    fun requireNested(role: String = "Link"): NestedLink = this as? NestedLink ?: error("$role has root parent.")

    /** Ancestor links, excluding this Link and stopping before [distinct]. */
    fun ancestors(): Sequence<NestedLink> = generateSequence(parentOrNull()?.nestedOrNull()) { it.parent().nestedOrNull() }

    @Deprecated("Redundant self-referencing") override val link: Link get() = this
}

/** Represents any object that is topologically grounded in a [Link] representation. */
interface Linkable { val link: Link }
/** Represents any object that is topologically grounded in a [NestedLink] representation. */
interface NestedLinkable { val link: NestedLink }

/** Link whose parent is known to be another `Link`, not root. */
sealed interface NestedLink : Link, NestedLinkable {
    fun parent(): Link
    override fun parentOrNull(): Link = parent()
    override fun nestedOrNull(): NestedLink = this
    @Deprecated("Redundant self-referencing") override val link: NestedLink; get() = this
}

/** Create a child under this link; the result is nested because this link is a non-root parent. */
fun Link.then(target: NestedLink): NestedLink = then(target as Link)
/** Build a link from nullable parent whose `null` value denotes the kernel root. */
fun Link?.then(target: Link?): Link
= this ?.then(target) ?: ROOT.then(target as? RawLink ?: return distinct())
/** Build a child link whose target link may be the kernel root. */
fun Link.then(target: Link?): NestedLink = (this as RawLink).then(target as? RawLink ?: ROOT) as NestedLink

/** The globally unique root endpoint, file-private so root cannot become a module-level Link value again. */
private val ROOT: RawLink = RootLink
private data object RootLink : RawLink { override fun toString(): String = "[ROOT]" }
/** The first distinct born from ROOT */
internal fun distinct(): Link = LinkInterner.get(ROOT, ROOT)

private sealed class ChildLink(protected val parent: RawLink, val target: RawLink) : Link, RawLink {
    override fun target(): Link = target as? Link ?: error("Link target is the root.")
    override fun nestedTargetOrNull(): NestedLink? = target as? NestedLink
    override fun requireNestedTarget(): NestedLink = target as? NestedLink ?: error("Link target has root parent.")
    override fun toString(): String = "[$parent -> $target]"
}

private class RootChildLink(target: RawLink) : ChildLink(ROOT, target) {
    override fun parentOrNull(): Link? = null
}
private class NestedChildLink(parent: RawLink, target: RawLink) : ChildLink(parent, target), NestedLink {
    override fun parent(): Link = parent as Link
    override fun parentOrNull(): Link = parent as Link
}

private sealed interface RawLink
private fun RawLink.then(target: RawLink): Link = LinkInterner.get(this, target)
/** Internal escape hatch for seed, record, and compact-codec code that must preserve private-root targets. */
@InternalTopologyApi fun Link.rawTargetOrNull(): Link? = (this as ChildLink).target as? Link

interface Anchor: NestedLinkable
interface LowLevelDimensionAnchor: Linkable
interface DimensionAnchor: LowLevelDimensionAnchor, Anchor {
    /** Create a cross-dimension anchor to reflect one dimension emergent / collapse into another */
    fun into(dimension: LowLevelDimensionAnchor): CrossDimensionAnchor
    operator fun invoke() = link
}

internal class Dimension internal constructor(selfReferenced: Link): DimensionAnchor {
    internal constructor(selfReferenced: LowLevelDimensionAnchor): this(selfReferenced.link)
    override fun into(dimension: LowLevelDimensionAnchor) = CrossDimensionAnchor(this, dimension)
    override val link = selfReferenced.then(selfReferenced)
}

// TODO: Revisit this global interner before production use.
// It keeps the prototype small and makes protocol-level equality direct, but a
// long-lived node may need weak interning, eviction, or a store-backed identity map.
private object LinkInterner {

    fun get(parent: RawLink, target: RawLink): Link
    = linksByParent.getOrPut(parent) { mutableMapOf() }.getOrPut(target) {
        if (parent !is Link) RootChildLink(target) else NestedChildLink(parent, target)
    }

    private val linksByParent = mutableMapOf<RawLink, MutableMap<RawLink, Link>>()
}

/**
 * Arena-local Link materialization kernel plus graph-free structural records at the boundary.
 *
 * It keeps the stricter "everything in the graph is Link topology" direction while separating graph-free
 * [Link] ontology from [MaterializedLink] runtime handles:
 *
 * - [Link] is the graph-free non-root topology object.
 * - [MaterializedLink] is the single local handle type for visible non-root Link structure in this arena.
 * - The root Link remains private kernel furniture used only while restoring or building raw structure.
 * - Graph-free exchange uses compact projections or direct [Link] objects, then restores into a local arena.
 *
 * The arena name is deliberate: Link identity is graph-free and durable, while this type only owns local
 * materialization handles and their runtime canonicalization.
 *
 * The surrounding Link layer is intentionally split into sibling files:
 *
 * - Genesis vocabulary creates the first structural distinctions from root alone.
 * - Codec/rendering turns boundary material such as bits and bytes into Link structure.
 * - Meaning protocols give selected Link shapes semantic roles.
 * - Projection/addressing derives external IDs from Link structure without storing them on Link.
 *
 * Keeping those layers outside this kernel is the point of the split. If this file starts importing ByteString,
 * naming signifier protocols, or choosing a digest algorithm, the protocol core has become too thick again.
 */
class LinkArena {

    /**
     * Materialized Link handle scoped to this [LinkArena] arena.
     *
     * Scope here means object ownership and canonicalization scope, not ontology. Two arenas may materialize handles
     * for the same graph-free [Link]. The handle keeps local runtime caches and validation explicit without forcing
     * protocol-level code to pretend the Link itself belongs to this arena.
     *
     * Kotlin's type system still sees every runtime handle as [LinkArena.MaterializedLink], not as a different type per
     * arena instance. Arena-local APIs use [requireLocal] when handle ownership matters, while graph-free construction
     * can operate on the underlying non-root [link] directly.
     */
    inner class MaterializedLink internal constructor(val link: Link) {

        fun target(): MaterializedLink = materialize(link.target())

        /**
         * Materialize a child Link under this Link handle.
         *
         * There is no raw value path through this function. Bits, bytes, strings, protocol names, and projections
         * must first be expressed as Link structure by layers outside this kernel.
         *
         * The target must already be materialized in this arena. If a caller holds only a graph-free [Link] or
         * a handle from another arena, it should cross the boundary explicitly through [materialize] instead of
         * being silently folded into this handle operation.
         */
        fun then(target: MaterializedLink): MaterializedLink {
            val localTarget = requireLocal(target, "Target Link")
            return materialize(link.then(localTarget.link))
        }

        /** The arena that materialized this runtime handle. */
        internal val arena: LinkArena get() = this@LinkArena

        /** Debug display only. It is not a stable encoding and must not be used as a Link ID. */
        override fun toString(): String = link.toString()
    }

    internal fun requireLocal(link: MaterializedLink, role: String = "Link")
    = link.also { require(link.arena === this) { "$role belongs to another LinkArena. Materialize its underlying Link in this LinkArena first." }}

    /** Materialize a non-root graph-free Link into this arena's canonical runtime handle. */
    fun materialize(link: Link): MaterializedLink
    = materializedLinks.getOrPut(link) { MaterializedLink(link) }

    /** Arena-local handle cache: one runtime object per graph-free non-root [Link] in this materialization context. */
    private val materializedLinks = mutableMapOf<Link, MaterializedLink>()
}

/**
 * Boundary-level equivalence of two [Link] structures.
 *
 * This is a recursive topological check that determines if two [Link] objects represent the same graph structure,
 * without depending on concrete process-local object identity or specific projection/hashing schemes.
 */
fun structurallyEquivalent(a: Link, b: Link): Boolean {
    if (a === b) return true

    val aParent = a.parentOrNull()
    val bParent = b.parentOrNull()
    if ((aParent == null) != (bParent == null)) return false
    if (aParent != null && bParent != null && ! structurallyEquivalent(aParent, bParent)) return false

    @OptIn(InternalTopologyApi::class) val aTarget = a.rawTargetOrNull()
    @OptIn(InternalTopologyApi::class) val bTarget = b.rawTargetOrNull()
    if ((aTarget == null) != (bTarget == null)) return false
    if (aTarget != null && bTarget != null && ! structurallyEquivalent(aTarget, bTarget)) return false

    return true
}

/** Structural equivalence helper for materialized handles. */
fun structurallyEquivalent(a: LinkArena.MaterializedLink, b: LinkArena.MaterializedLink): Boolean = a.link == b.link

@Retention(AnnotationRetention.BINARY) @Target(AnnotationTarget.CLASS, AnnotationTarget.FUNCTION)
@RequiresOptIn(level = RequiresOptIn.Level.ERROR,
    message = "This API is for internal LinkTopology constraints and is hidden from public use.")
annotation class InternalTopologyApi
